import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { seoArticles } from "@/mocks/seo-articles";

const siteUrl = import.meta.env.VITE_SITE_URL;

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main>
          <section className="py-24 md:py-32 bg-background-50 text-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl mx-auto">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                  Article Not Found
                </h1>
                <p className="text-sm text-foreground-600 mb-8">
                  The guide you are looking for does not exist or may have been moved.
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md hover:bg-primary-600 transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Kakobuy Spreadsheet
                </Link>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        {/* Article Hero */}
        <section className="relative w-full min-h-[420px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.heroImageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/35"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-2 text-white/70 text-xs font-medium mb-4">
                <Link to="/" className="hover:text-white transition-colors">
                  Home
                </Link>
                <span>/</span>
                <span className="text-white/90">Guide</span>
              </div>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                {article.title}
              </h1>
              <p className="text-sm md:text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
                {article.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              {article.sections.map((section, idx) => (
                <div key={idx} className="mb-12 md:mb-14">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5 leading-tight">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((para, pIdx) => (
                    <p
                      key={pIdx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4"
                    >
                      {para}
                    </p>
                  ))}
                </div>
              ))}

              {/* Back to Home CTA */}
              <div className="mt-14 pt-10 border-t border-background-200/70 text-center">
                <p className="text-sm text-foreground-600 mb-4">
                  Ready to start shopping with verified sellers?
                </p>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-md hover:bg-primary-600 transition-colors text-sm font-semibold whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to Kakobuy Spreadsheet
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* More Guides */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                <div>
                  <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">
                    Continue Reading
                  </p>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    More Kakobuy Guides
                  </h2>
                </div>
                <Link
                  to="/"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  View All &rarr;
                </Link>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {seoArticles
                  .filter((a) => a.slug !== slug)
                  .slice(0, 3)
                  .map((guide) => (
                    <Link
                      key={guide.slug}
                      to={`/guide/${guide.slug}`}
                      className="group block bg-background-100 rounded-xl p-6 border border-background-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer"
                    >
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {guide.subtitle.length > 100
                          ? guide.subtitle.slice(0, 100) + "..."
                          : guide.subtitle}
                      </p>
                      <span className="inline-flex items-center gap-1 mt-4 text-xs font-medium text-primary-500 group-hover:text-primary-600 transition-colors">
                        Read More <i className="ri-arrow-right-line"></i>
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Article Schema - Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": article.title,
            "description": article.subtitle,
            "author": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet",
              "url": siteUrl,
            },
            "publisher": {
              "@type": "Organization",
              "name": "Kakobuy Spreadsheet",
              "url": siteUrl,
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
              },
            },
            "datePublished": "2026-06-25",
            "dateModified": "2026-06-25",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${siteUrl}/guide/${article.slug}`,
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": `${siteUrl}/`,
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": article.title,
                "item": `${siteUrl}/guide/${article.slug}`,
              },
            ],
          }),
        }}
      />
    </>
  );
}